import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";

import { ConnectionStatus } from "@/components/trade/ConnectionStatus";
import { IndicatorsPanel } from "@/components/trade/IndicatorsPanel";
import { MarketSelector } from "@/components/trade/MarketSelector";
import { PriceChart } from "@/components/trade/PriceChart";
import { TimeframeSelector } from "@/components/trade/TimeframeSelector";
import { PaperTradingPanel } from "@/components/paper-trading";
import { useTradeData } from "@/hooks/useTradeData";
import { useTradeStore } from "@/stores/tradeStore";
import { trackEvent } from "@/lib/mixpanel";
import { ArrowsPointingOutIcon, ArrowsPointingInIcon } from "@heroicons/react/24/outline";

export default function Trade() {
  const [mounted, setMounted] = useState(false);
  const { market, timeframe, indicators } = useTradeStore();
  const { data, lastPrice, reload, loading, error, dataSource, isConnected, latency } =
    useTradeData(market, timeframe);
  const [isFullScreen, setIsFullScreen] = useState(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    trackEvent("Page Viewed", { page_name: "trade", market, timeframe });
  }, [market, timeframe]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsFullScreen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Calculate percentage change based on first candle open vs current price
  const priceChange = useMemo(() => {
    if (!data.length || !lastPrice) return { value: 0, percent: 0 };
    const firstOpen = data[0].open;
    const change = lastPrice - firstOpen;
    const percent = (change / firstOpen) * 100;
    return { value: change, percent };
  }, [data, lastPrice]);

  const renderLastPrice = () => {
    if (!mounted || !data.length) return "--";
    return lastPrice.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  if (!mounted) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-8">
        <div className="h-6 w-40 animate-pulse rounded bg-slate-200" />
        <div className="mt-4 h-96 animate-pulse rounded-2xl border border-slate-200 bg-slate-100" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 text-white">
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-3">
          <h1 className="text-3xl font-bold text-white">Trade</h1>
          <p className="text-sm text-slate-200">
            Real-time price action with lightweight charts and quick market switching.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <MarketSelector />
            <div className="rounded-xl border border-slate-200/60 bg-white/10 px-3 py-2 text-sm font-semibold text-white shadow-sm backdrop-blur">
              <span>Last: ${renderLastPrice()}</span>
              {mounted && data.length > 0 && (
                <span
                  className={clsx(
                    "ml-2",
                    priceChange.percent >= 0 ? "text-emerald-400" : "text-rose-400"
                  )}
                >
                  {priceChange.percent >= 0 ? "+" : ""}
                  {priceChange.percent.toFixed(2)}%
                </span>
              )}
            </div>
            <ConnectionStatus
              isConnected={isConnected}
              latency={latency}
              dataSource={dataSource}
            />
          </div>
        </div>
        <TimeframeSelector />
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <strong className="font-semibold">Error loading data:</strong> {error}
        </div>
      )}

      {/* Main trading layout: Chart + Paper Trading Panel */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Chart section - takes 2/3 on large screens */}
        <div className="lg:col-span-2">
          <PriceChart
            candles={data}
            indicators={indicators}
            loading={loading || !data.length}
            isFullScreen={isFullScreen}
            onExitFullScreen={() => setIsFullScreen(false)}
          />

          <div className="mt-4 flex items-center justify-between">
            <IndicatorsPanel />
            <div className="flex gap-2">
              <button
                onClick={reload}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300"
              >
                Refresh Data
              </button>
              <button
                onClick={() => setIsFullScreen((v) => !v)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300"
              >
                {isFullScreen ? (
                  <span className="flex items-center gap-2">
                    <ArrowsPointingInIcon className="h-4 w-4" /> Exit Fullscreen
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <ArrowsPointingOutIcon className="h-4 w-4" /> Fullscreen
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Paper Trading Panel - takes 1/3 on large screens */}
        <div className="lg:col-span-1">
          <PaperTradingPanel currentPrice={lastPrice} />
        </div>
      </div>
    </div>
  );
}
